import React, { useEffect, useRef, useState } from 'react'
import { Badge, Avatar, List, Button, Popover } from 'antd'
import {
  BellOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  MailOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import Flex from 'components/shared-components/Flex'
import { formatDisplayDateTime } from 'components/shared-components/DateTimeDisplay'
import notificationService from 'services/notification'
import { Link } from 'react-router-dom'
import { getPageListPath } from 'constants/PageRoutes'
import { notificationKey } from 'constants/RoutesKeys'

const getIcon = (icon) => {
  switch (icon) {
    case 'mail':
      return <MailOutlined />
    case 'alert':
      return <WarningOutlined />
    case 'check':
      return <CheckCircleOutlined />
    default:
      return <BellOutlined />
  }
}

export const NavNotification = () => {
  const [visible, setVisible] = useState(false)
  const [list, setList] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [viewedIds, setViewedIds] = useState(new Set())
  const observerRef = useRef(null)
  const notificationBodyRef = useRef(null)
  const [clearLoading, setClearLoading] = useState(false)
  const [notificationsLoading, setNotificationsLoading] = useState(false)

  const getNotifications = async () => {
    setNotificationsLoading(true)
    try {
      const res = await notificationService.get('?page=1&read=false')
      if (res?.data) {
        setList(res.data)
        setTotalCount(res.total ?? res.count ?? res.data.length ?? 0)
      } else {
        setList([])
        setTotalCount(0)
      }
    } catch (err) {
      console.error('Failed to load notifications', err)
    } finally {
      setNotificationsLoading(false)
    }
  }

  useEffect(() => {
    getNotifications()
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      getNotifications()
    }, 30 * 60 * 1000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const notificationId = entry.target.getAttribute('id')
            if (notificationId && !viewedIds.has(notificationId)) {
              setViewedIds((prev) => new Set([...prev, notificationId]))
            }
          }
        })
      },
      {
        root: notificationBodyRef.current,
        threshold: 0.8,
      }
    )

    return () => {
      observerRef.current?.disconnect()
    }
  }, [])

  const markAllRead = async () => {
    setClearLoading(true)
    try {
      const res = await notificationService.readAll()
      if (res?.success) {
        setViewedIds(new Set())
        await getNotifications()
      }
    } catch (err) {
      console.error('Failed to clear notifications', err)
    } finally {
      setClearLoading(false)
    }
  }

  const markReadNotificationIds = async () => {
    if (viewedIds.size === 0) return
    try {
      const res = await notificationService.readIds({
        notificationIds: [...viewedIds],
      })
      if (res?.success) {
        setViewedIds(new Set())
        await getNotifications()
      }
    } catch (err) {
      console.error('Failed to mark notifications read', err)
    }
  }

  useEffect(() => {
    if (!visible || !notificationBodyRef.current || !observerRef.current) return

    observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const notificationId = entry.target.getAttribute('id')
            if (notificationId) {
              setViewedIds((prev) => {
                if (prev.has(notificationId)) return prev
                return new Set([...prev, notificationId])
              })
            }
          }
        })
      },
      {
        root: notificationBodyRef.current,
        threshold: 0.8,
      }
    )

    const items = notificationBodyRef.current.querySelectorAll('.custom-list-item')
    items.forEach((item) => observerRef.current.observe(item))

    return () => observerRef.current?.disconnect()
  }, [visible, list])

  const getNotificationBody = (items) => {
    if (notificationsLoading && !items?.length) {
      return (
        <div style={{ margin: '20px 0' }}>
          <Flex alignItems="center" justifyContent="center">
            <LoadingOutlined style={{ fontSize: 24 }} />
          </Flex>
        </div>
      )
    }

    if (!notificationsLoading && !items?.length) {
      return (
        <div className="empty-notification">
          <img
            src="https://gw.alipayobjects.com/zos/rmsportal/sAuJeJzSKbUmHfBQRzmZ.svg"
            alt="empty"
          />
          <p className="mt-3">You have viewed all notifications</p>
        </div>
      )
    }

    if (!items?.length) return null

    return (
      <List
        size="small"
        itemLayout="horizontal"
        dataSource={items}
        renderItem={(item) => (
          <List.Item id={item._id} className="custom-list-item">
            <Flex flexDirection="column" className="custom-width">
              <Flex alignItems="center">
                <div className="pr-3">
                  {item.img ? (
                    <Avatar src={`/img/avatars/${item.img}`} />
                  ) : (
                    <Avatar
                      className={`ant-avatar-${item.type}`}
                      icon={getIcon(item.icon)}
                    />
                  )}
                </div>
                <div className="mr-3">
                  <span className="font-weight-bold text-dark">
                    {item.name}{' '}
                  </span>
                  <span>{item.description}</span>
                </div>
              </Flex>
              <small className="ml-auto mt-2">
                {formatDisplayDateTime(item.createdAt)}
              </small>
            </Flex>
          </List.Item>
        )}
      />
    )
  }

  const handleVisibleChange = async (value) => {
    if (!value) {
      await markReadNotificationIds()
    }
    setVisible(value)
  }

  const viewAllPath = getPageListPath(notificationKey)

  const notificationList = (
    <div>
      <div className="nav-notification-header d-flex justify-content-between align-items-center">
        <h4 className="mb-0">Notification</h4>
        <Button
          className="text-primary"
          type="text"
          onClick={markAllRead}
          loading={clearLoading}
          size="small"
        >
          Clear
        </Button>
      </div>
      <div className="nav-notification-body" ref={notificationBodyRef}>
        {getNotificationBody(list)}
      </div>
      {list?.length > 0 ? (
        <div className="nav-notification-footer">
          <Link
            className="d-block"
            to={viewAllPath}
            onClick={() => handleVisibleChange(false)}
          >
            View all
          </Link>
        </div>
      ) : null}
    </div>
  )

  return (
    <Popover
      placement="bottomRight"
      open={visible}
      onOpenChange={handleVisibleChange}
      title={null}
      content={notificationList}
      trigger="click"
      classNames={{ root: 'nav-notification custom-nav-notification' }}
    >
      <div
        className="nav-item"
        onClick={() => {
          setVisible(true)
          getNotifications()
        }}
      >
        <Badge count={totalCount}>
          <BellOutlined className="nav-icon" style={{ fontSize: 20 }} />
        </Badge>
      </div>
    </Popover>
  )
}

export default NavNotification
